import { databaseService } from './DatabaseService.js';
import DatabaseQuery from './DatabaseQuery.js';
import { withWildcards, withGrammaticalVariants } from './QueryExpanders.js';
import UrantiaBookRangedReference from './UrantiaBookRangedReference.js';

export const searchService = {

  /* 
   * Search for a single term in the index. No spaces allowed. It's a direct token lookup.
   * @param {string} term - The term to search for
   * @param {Object} options - Options for the search
   * @param {number} options.limitSearchResults - The maximum number of results to return
   * @returns {Object} - An object containing the search results
   */
  async search(searchExpression, options = {limitSearchResults: 1000}) {
    const startTime = performance.now();
    let searchLog = "";
    searchLog += `Search expression: ${searchExpression}\n`;
    
    const databaseQuery = new DatabaseQuery(searchExpression)
      .cleanRawQuery()
      .removeIllegalCharacters()
      .buildListsOfTerms()
      .expand(withWildcards)
      .expand(withGrammaticalVariants)
      // @todo: handle equivalence map
      .validate();
    
    const compiledQuery = databaseQuery.compile();
    searchLog += `Compiled query: ${JSON.stringify(compiledQuery, null, 2)}\n`;

    if (!compiledQuery.isValid) {
      searchLog += `Search errors: ${compiledQuery.errors}\n`;
      return {
        success: false,
        errors: compiledQuery.errors,
        results: [],
        isPureTextResult: false,
        searchLog: searchLog
      };
    }

    // Build a list of token groups to search for.
    const tokenGroups = [
      ...compiledQuery.expandedSimpleTokens,
      ...compiledQuery.tokensInQuotedTerms.map(token => [token]),
      ...compiledQuery.expandedWildcardTokens
    ];
    
    searchLog += `---Rough search---\n`;
    
    await databaseService.loadDatabase();
    let roughResults = null;

    // Use provided SRT reference as the nucleus of the search, if it was provided.
    if (compiledQuery.srtTerms?.[0]) {
      const rangedReference = new UrantiaBookRangedReference(compiledQuery.srtTerms[0]);
      const startParagraphId = databaseService
        .getParagraphIdForPaperSectionParagraph(rangedReference.startPaper, rangedReference.startSection, rangedReference.startParagraph);
      const endParagraphId = databaseService
        .getParagraphIdForPaperSectionParagraph(rangedReference.endPaper, rangedReference.endSection, rangedReference.endParagraph);

      searchLog += `Search constrained to text from ` + 
        `${rangedReference.startPaper}:${rangedReference.startSection}.${rangedReference.startParagraph} ` +
        `to ${rangedReference.endPaper}:${rangedReference.endSection}.${rangedReference.endParagraph}\n`;

      // Paragraph IDs from start to end now are the beginning of our rough results
      const paragraphCount = endParagraphId - startParagraphId + 1;
      roughResults = [...Array(paragraphCount)].map((_, i) => startParagraphId + i);
    }

    let n = 1;
    for (const tokensToUnion of tokenGroups) {
      const paragraphsForTokenGroup = new Set();
      for (const token of tokensToUnion) {
        const paragraphIds = await databaseService.getParagraphIdsForToken(token);
        paragraphIds.forEach(id => paragraphsForTokenGroup.add(id));
      }
      searchLog += `Group ${n}: ${paragraphsForTokenGroup.size} paragraphs contain any of: ${tokensToUnion.join(', ')}\n`;
      n++;

      // If this is the first token, its results become the initial set
      if (roughResults === null) {
        roughResults = paragraphsForTokenGroup;
      } else {
        // For subsequent tokens, keep only results that are in both sets (intersection)
        const previousNumResults = roughResults.size;
        roughResults = new Set([...roughResults].filter(x => paragraphsForTokenGroup.has(x)));
        searchLog += `  -> Of these, ${roughResults.size} were also a part of the previous set\n`;
      }
    }

    searchLog += `---End of rough search. Found ${roughResults?.size} paragraphs---\n`;

    // If no results found, return empty array
    if (!roughResults || roughResults.size === 0) {
      searchLog += `No results found\n`;
      return {
        success: true,
        results: [],
        isPureTextResult: false,
        totalCount: 0,
        searchLog: searchLog
      };
    }

    const isPureTextResult = compiledQuery.srtTerms?.length === 1 &&
                             compiledQuery.simpleTokens?.length === 0 &&
                             compiledQuery.wildcardTerms?.length === 0 &&
                             compiledQuery.quotedTerms?.length === 0;
    
    // Get actual book content, sorted in ascending order
    const sortedRoughResults = Array.from(roughResults).sort((a, b) => a - b);
    const textResults = sortedRoughResults.slice(0, options.limitSearchResults).map(id => databaseService.getParagraphTextById(id));
    searchLog += `Found ${textResults.length} results in ${sortedRoughResults.length} paragraph IDs\n`;

    // Add execution time to log
    const endTime = performance.now();
    const executionTimeMs = Math.round((endTime - startTime));
    searchLog += `Total execution time: ${executionTimeMs} milliseconds\n`;

    return {
      success: true,
      results: textResults,
      isPureTextResult: isPureTextResult,
      totalCount: sortedRoughResults.length,
      searchLog: searchLog
    };
  },

};

// Export a default service to use (can be switched later)
export default searchService;
